// Shared API error helpers.
//
// This used to be a copy-pasted `extractErrorMessage(error: any, ...)` in twelve separate hook
// files - all byte-for-byte identical. One typed copy lives here instead.
//
// Why it exists at all: mutations used to show a fixed generic message on failure ("שגיאה ביצירת
// הטיקט", "שגיאה ביצירת החשבונית מהמכירה") which hid the real, often self-explanatory reason the
// backend rejected the request - a domain rule like "חובה לבחור לפחות קטגוריית בעיה אחת" or
// "מכירה ללא לקוח - לא ניתן ליצור חשבונית". Real failures were undiagnosable without server logs.
// Callers pass the old generic text as `fallback`, used only when the backend gave nothing usable.

/**
 * The body the API returns on a failed request: either our own ApiResponse envelope (`message`)
 * or an ASP.NET validation problem (`errors`, keyed by field name with one message array each).
 */
export interface ApiErrorBody {
  message?: string;
  errorCode?: string;
  error?: string;
  errors?: Record<string, string[]>;
}

/**
 * Best user-facing message for a failed request: the first field-level validation error if the
 * server sent one, otherwise the top-level message, otherwise the caller's fallback.
 *
 * Takes `unknown` because a rejected promise can be anything at all. Narrowing is structural
 * rather than via axios's `isAxiosError` on purpose - that would require the `isAxiosError` flag
 * to be present, which plain mock rejections in tests don't set.
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: ApiErrorBody } } | null | undefined)?.response?.data;
  const firstFieldError = data?.errors && Object.values(data.errors)[0];
  if (Array.isArray(firstFieldError) && firstFieldError[0]) return firstFieldError[0];
  return data?.message || fallback;
}

/** The ApiResponse envelope's errorCode, for callers that react to a specific refusal. */
export function extractErrorCode(error: unknown): string | undefined {
  return (error as { response?: { data?: ApiErrorBody } } | null | undefined)?.response?.data?.errorCode;
}
